/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild, ViewEncapsulation, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { AuthenticationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { MenuPermissionsService } from '../menu-permissions/menu-permissions.service';
import { DocumentSearchService, EMPTY_CRITERIA, SearchHit } from '../search/document-search.service';
import { WorkflowSettingsService } from '../workflow/workflow-settings.service';
import { CommandPaletteService } from './command-palette.service';

/** One place the palette can send the keyboard to. */
export interface PaletteCommand {
  key: string;
  icon: string;
  labelKey: string;
  route: string;
  /** Extra words a query also matches, e.g. the English name while the UI is Thai. */
  keywords?: string;
}

type PaletteRow = { kind: 'cmd'; command: PaletteCommand; label: string } | { kind: 'doc'; hit: SearchHit };

/**
 * Ctrl+K / Cmd+K command palette — pages the viewer's menus allow, plus a
 * quick document search, in one keyboard-driven list.
 *
 * The command list is not hand-maintained: it is the same `MENU_ITEMS` the
 * sidebar and the route guards read, filtered by `isMenuAllowed`, so a menu
 * an administrator closed to this user never shows up here either. Workflow
 * entries honour the same feature flag the sidebar does. Search hits open the
 * document preview (or the folder) exactly as the search page does.
 *
 * The palette is mounted once in `app.component`; it stays inert on the login
 * screen and for signed-out visitors.
 */
@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss']
})
export class CommandPaletteComponent implements OnInit {
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly menus = inject(MenuPermissionsService);
  private readonly workflowSettings = inject(WorkflowSettingsService);
  private readonly searchService = inject(DocumentSearchService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly paletteRequests = inject(CommandPaletteService);

  @ViewChild('queryInput') private readonly queryInput?: ElementRef<HTMLInputElement>;

  /** Material icons for the sidebar's menu keys — same glyphs the navbar shows. */
  private static readonly ICONS: Record<string, string> = {
    insights: 'insights',
    upload: 'cloud_upload',
    all: 'description',
    recent: 'schedule',
    search: 'search',
    favorites: 'star_border',
    startwf: 'play_circle',
    work: 'inbox',
    track: 'timeline',
    manage: 'account_tree',
    repository: 'inventory_2',
    copymove: 'drive_file_move',
    trash: 'delete_outline',
    settings: 'settings'
  };

  /** Menu keys the workflow feature flag controls — mirrors MenuPermissionsService.featureOpen. */
  private static readonly WORKFLOW_KEYS = new Set(['work', 'approvals', 'startwf', 'track', 'manage']);

  /** 'approvals' is a kept-for-policies alias of my-work; listing it twice helps nobody. */
  private static readonly SKIP = new Set(['approvals']);

  /** Always-available destinations that are not governed by menu policies. */
  private static readonly EXTRA: PaletteCommand[] = [
    { key: 'dashboard', icon: 'home', labelKey: 'DMS.NAV.HOME', route: 'dashboard', keywords: 'home overview' },
    { key: 'personal', icon: 'folder_shared', labelKey: 'APP.BROWSE.PERSONAL.TITLE', route: 'personal-files', keywords: 'personal files my' },
    { key: 'libraries', icon: 'library_books', labelKey: 'APP.BROWSE.LIBRARIES.TITLE', route: 'libraries', keywords: 'file libraries sites' },
    { key: 'shared', icon: 'share', labelKey: 'DMS.NAV.SHARE_LINKS', route: 'share-links', keywords: 'shared files share links ลิงก์แชร์' }
  ];

  readonly signedIn = signal(false);
  readonly open = signal(false);
  readonly rows = signal<PaletteRow[]>([]);
  readonly hits = signal<SearchHit[]>([]);
  readonly searching = signal(false);
  readonly selected = signal(0);

  query = '';
  private readonly typed = new Subject<string>();

  ngOnInit(): void {
    this.signedIn.set(this.auth.isLoggedIn());
    this.auth.onLogin.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.signedIn.set(true));
    this.auth.onLogout.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.signedIn.set(false);
      this.close();
    });
    // the help menu asks for it too
    this.paletteRequests.requested.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.open()) {
        this.toggle();
      }
    });

    this.typed
      .pipe(
        debounceTime(250),
        switchMap((q) => {
          if (q.trim().length < 2) {
            return of({ hits: [] });
          }
          this.searching.set(true);
          return this.searchService.search({ ...EMPTY_CRITERIA, term: q }, 'score', 0, 5).pipe(catchError(() => of({ hits: [] })));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ hits }) => {
        this.searching.set(false);
        this.hits.set(hits);
        this.rebuild();
      });
  }

  /** Every command the signed-in person may run — sidebar rules and the workflow flag applied. */
  commands(): PaletteCommand[] {
    const policy = MenuPermissionsService.MENU_ITEMS.filter(
      (m) => m.kind === 'nav' && !CommandPaletteComponent.SKIP.has(m.key) && m.route && this.menus.isMenuAllowed(m.id)
    )
      .filter((m) => this.workflowSettings.enabled() || !CommandPaletteComponent.WORKFLOW_KEYS.has(m.key))
      .map<PaletteCommand>((m) => ({
        key: m.key,
        icon: CommandPaletteComponent.ICONS[m.key] ?? 'chevron_right',
        labelKey: m.titleKey,
        route: m.route ?? '',
        keywords: m.key
      }));
    return [...CommandPaletteComponent.EXTRA, ...policy];
  }

  /** Commands whose label, key, route or keywords contain the query. */
  matching(query: string): PaletteCommand[] {
    const q = query.trim().toLowerCase();
    const all = this.commands();
    if (!q) {
      return all;
    }
    return all.filter((c) => `${this.translate.instant(c.labelKey)} ${c.key} ${c.route} ${c.keywords ?? ''}`.toLowerCase().includes(q));
  }

  /** Rebuilds the flat row list from the current query — commands first, then documents. */
  rebuild(): void {
    const rows: PaletteRow[] = this.matching(this.query).map((command) => ({
      kind: 'cmd',
      command,
      label: this.translate.instant(command.labelKey)
    }));
    if (this.query.trim().length >= 2) {
      rows.push(...this.hits().map<PaletteRow>((hit) => ({ kind: 'doc', hit })));
    }
    this.rows.set(rows);
    if (this.selected() >= rows.length) {
      this.selected.set(0);
    }
  }

  onQuery(value: string): void {
    this.query = value;
    this.rebuild();
    this.typed.next(value);
  }

  @HostListener('document:keydown.control.k')
  @HostListener('document:keydown.meta.k')
  toggle(event?: Event): void {
    event?.preventDefault();
    if (!this.signedIn()) {
      return;
    }
    if (this.open()) {
      this.close();
      return;
    }
    this.query = '';
    this.hits.set([]);
    this.open.set(true);
    this.rebuild();
    setTimeout(() => this.queryInput?.nativeElement.focus());
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) {
      this.close();
    }
  }

  onKey(event: KeyboardEvent): void {
    const rows = this.rows();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selected.update((i) => (rows.length ? (i + 1) % rows.length : 0));
      this.scrollSelected();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selected.update((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
      this.scrollSelected();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = rows[this.selected()];
      if (row) {
        this.activate(row);
      }
    }
  }

  activate(row: PaletteRow): void {
    this.close();
    if (row.kind === 'cmd') {
      void this.router.navigate(['/' + row.command.route]);
    } else {
      void this.router.navigate(row.hit.isFolder ? ['/personal-files', row.hit.id] : ['/document', row.hit.id]);
    }
  }

  close(): void {
    this.open.set(false);
  }

  indexOf(row: PaletteRow): number {
    return this.rows().indexOf(row);
  }

  private scrollSelected(): void {
    document.querySelector('.aca-palette__row--sel')?.scrollIntoView({ block: 'nearest' });
  }
}
